import type { PlayerInfo, SeriesInfo, SeriesStanding } from "../src/protocol";
import type { ThemeChoice } from "../src/themes";
import { openDialog, type DialogHandle } from "./dialog";
import { h } from "./dom";
import { outcome, positions, type RankingEntry } from "./ranking";
import { shareButton, type ResultCard } from "./shareCard";
import { STRINGS } from "./strings";
import { selectedTheme, themeSelect } from "./themePicker";

export interface ResultsOptions {
  /** This round's ranking. */
  ranking: RankingEntry[];
  /** Where the room is in its series of rounds (length 1: a single round, as before). */
  series: SeriesInfo;
  players: Map<string, PlayerInfo>;
  you: string;
  onRematch(theme: ThemeChoice): void;
  /** What "Share result" draws. */
  card: ResultCard;
}

const REMATCH_RETRY_MS = 4000;

/**
 * Results modal. A single round shows its ranking; a round in the middle of a series adds
 * the standings so far; the last round of a series shows the final standings, with every
 * round's points and the total. Ties share a position.
 */
export function openResults(opts: ResultsOptions): DialogHandle {
  const { ranking, series, players, you } = opts;
  const inSeries = series.length > 1;
  const final = inSeries && series.index >= series.length;

  let title: string = STRINGS.resultsTitle;
  let headline: string;
  const body: HTMLElement[] = [];
  if (final) {
    title = STRINGS.finalResults;
    const totals = series.standings.map((s) => ({ id: s.id, name: s.name, score: s.total }));
    const result = outcome(totals, you);
    headline =
      result.kind === "tie"
        ? STRINGS.seriesTie
        : result.kind === "you"
          ? STRINGS.seriesYouWon
          : STRINGS.seriesWinner(result.name);
    body.push(
      h("h3", { class: "panel-title" }, STRINGS.standingsTitle),
      standingsTable(series.standings, you, true),
    );
  } else {
    if (inSeries) title = STRINGS.roundDone(series.index, series.length);
    const result = outcome(ranking, you);
    headline =
      result.kind === "tie" ? STRINGS.tie : result.kind === "you" ? STRINGS.youWon : STRINGS.winner(result.name);
    body.push(rankingList(ranking, players, you));
    if (inSeries) {
      body.push(
        h("h3", { class: "panel-title" }, STRINGS.standingsSoFar),
        standingsTable(series.standings, you, false),
      );
    }
  }

  // Mid-series: on to the next round. Otherwise a fresh start (a new series if there was one).
  const rematchLabel = !inSeries
    ? STRINGS.rematch
    : final
      ? STRINGS.newSeries
      : STRINGS.nextRound(series.index + 1, series.length);
  const themeInput = themeSelect("rematch-theme");
  const rematchButton = h("button", { type: "button", class: "btn btn-primary" }, rematchLabel);
  rematchButton.addEventListener("click", () => {
    rematchButton.disabled = true;
    rematchButton.textContent = STRINGS.rematchPending;
    opts.onRematch(selectedTheme(themeInput));
    // If no new round arrives (e.g. the message was lost while reconnecting), allow retrying.
    setTimeout(() => {
      rematchButton.disabled = false;
      rematchButton.textContent = rematchLabel;
    }, REMATCH_RETRY_MS);
  });
  const viewBoardButton = h("button", { type: "button", class: "btn" }, STRINGS.viewBoard);

  const dialog = openDialog(
    final ? "results results-final" : "results",
    h("h2", { class: "dialog-title" }, title),
    h("p", { class: "results-headline" }, headline),
    ...body,
    h(
      "div",
      { class: "rematch-theme" },
      h(
        "label",
        { for: "rematch-theme" },
        inSeries && !final ? STRINGS.nextRoundTheme : STRINGS.rematchTheme,
      ),
      themeInput,
    ),
    h("div", { class: "dialog-actions" }, rematchButton, viewBoardButton),
    shareButton(() => opts.card),
  );
  viewBoardButton.addEventListener("click", () => dialog.close());
  rematchButton.focus();
  return dialog;
}

/** One round's ranking: place, avatar, name, points. */
function rankingList(ranking: RankingEntry[], players: Map<string, PlayerInfo>, you: string): HTMLElement {
  const places = positions(ranking);
  return h(
    "ol",
    { class: "ranking" },
    ...ranking.map((entry, i) => {
      const position = places[i]!;
      const color = players.get(entry.id)?.color;
      const li = h(
        "li",
        { class: position === 1 ? "ranking-row ranking-first" : "ranking-row" },
        h("span", { class: "ranking-pos" }, `${position}º`),
        h("span", { class: "avatar", "aria-hidden": "true" }, initial(entry.name)),
        h(
          "span",
          { class: "player-name" },
          entry.name,
          entry.id === you ? h("span", { class: "tag" }, STRINGS.you) : null,
        ),
        h("span", { class: "player-score" }, STRINGS.points(entry.score)),
      );
      if (color) li.style.setProperty("--player", color);
      return li;
    }),
  );
}

/**
 * Series standings: place, name, total and, with `perRound`, a column for each round. The
 * table scrolls sideways on narrow screens rather than squeezing the names.
 */
function standingsTable(standings: SeriesStanding[], you: string, perRound: boolean): HTMLElement {
  const places = positions(standings.map((s) => ({ id: s.id, name: s.name, score: s.total })));
  const rounds = perRound ? (standings[0]?.scores.length ?? 0) : 0;
  const roundHeads = Array.from({ length: rounds }, (_, i) =>
    h("th", { scope: "col", class: "num" }, STRINGS.roundColumn(i + 1)),
  );
  const table = h(
    "table",
    { class: "standings" },
    h(
      "thead",
      {},
      h(
        "tr",
        {},
        h("th", { scope: "col", class: "num" }, "#"),
        h("th", { scope: "col" }, ""),
        ...roundHeads,
        h("th", { scope: "col", class: "num" }, STRINGS.totalColumn),
      ),
    ),
    h(
      "tbody",
      {},
      ...standings.map((s, i) => {
        const row = h(
          "tr",
          { class: places[i] === 1 ? "standings-first" : "" },
          h("td", { class: "num" }, `${places[i]}º`),
          h(
            "th",
            { scope: "row" },
            h("span", { class: "avatar", "aria-hidden": "true" }, initial(s.name)),
            h("span", { class: "player-name" }, s.name),
            s.id === you ? h("span", { class: "tag" }, STRINGS.you) : null,
          ),
          ...(perRound ? s.scores.map((score) => h("td", { class: "num" }, String(score))) : []),
          h("td", { class: "num standings-total" }, String(s.total)),
        );
        row.style.setProperty("--player", s.color);
        return row;
      }),
    ),
  );
  return h("div", { class: "standings-wrap" }, table);
}

function initial(name: string): string {
  return [...name][0]?.toUpperCase() ?? "?";
}
